import React, { useRef } from 'react'
import { IconButton } from '../../../ui'

const SESSION_COLOR = '#9b6cc4'

export function CheckboxOwnerList({ items = [], onChange, maxItems = 20, maxChars = 140, readOnly = false }) {
  const inputRefs = useRef([])

  function update(index, patch) {
    const next = items.map((item, i) => (i === index ? { ...item, ...patch } : item))
    onChange({ items: next })
  }

  function handleAdd() {
    if (items.length >= maxItems) return
    onChange({ items: [...items, { text: '', done: false, ownerReport: false, ownerManager: false }] })
    setTimeout(() => inputRefs.current[items.length]?.focus(), 0)
  }

  function handleRemove(index) {
    onChange({ items: items.filter((_, i) => i !== index) })
  }

  function handleKeyDown(e, index) {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    } else if (e.key === 'Backspace' && items[index]?.text === '' && items.length > 0) {
      e.preventDefault()
      handleRemove(index)
      setTimeout(() => inputRefs.current[Math.max(0, index - 1)]?.focus(), 0)
    }
  }

  return (
    <div style={styles.wrap}>
      {items.map((item, index) => (
        <div key={index} style={styles.row}>
          <button
            style={{
              ...styles.checkbox,
              ...(item.done ? styles.checkboxDone : {}),
            }}
            onClick={() => !readOnly && update(index, { done: !item.done })}
            disabled={readOnly}
            aria-label={item.done ? 'Mark not done' : 'Mark done'}
          >
            {item.done && <span style={styles.checkmark}>✓</span>}
          </button>
          <input
            ref={(el) => { inputRefs.current[index] = el }}
            style={{
              ...styles.input,
              opacity: item.done ? 0.5 : 1,
            }}
            value={item.text}
            maxLength={maxChars}
            disabled={readOnly}
            placeholder={readOnly ? '' : 'Action item…'}
            onChange={(e) => update(index, { text: e.target.value })}
            onKeyDown={(e) => handleKeyDown(e, index)}
            aria-label={`Action item ${index + 1}`}
          />
          <div style={styles.ownerBadges}>
            <button
              style={{
                ...styles.badge,
                ...(item.ownerReport ? styles.badgeActive : styles.badgeInactive),
              }}
              onClick={() => !readOnly && update(index, { ownerReport: !item.ownerReport })}
              disabled={readOnly}
              aria-label="Toggle report ownership"
              aria-pressed={item.ownerReport}
            >R</button>
            <button
              style={{
                ...styles.badge,
                ...(item.ownerManager ? styles.badgeActive : styles.badgeInactive),
              }}
              onClick={() => !readOnly && update(index, { ownerManager: !item.ownerManager })}
              disabled={readOnly}
              aria-label="Toggle manager ownership"
              aria-pressed={item.ownerManager}
            >M</button>
          </div>
          {!readOnly && (
            <IconButton ariaLabel="Remove item" onClick={() => handleRemove(index)} tabIndex={-1}>×</IconButton>
          )}
        </div>
      ))}
      {!readOnly && items.length < maxItems && (
        <button style={styles.addBtn} onClick={handleAdd}>+ add action</button>
      )}
    </div>
  )
}

const styles = {
  wrap: { padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: '5px' },
  row: { display: 'flex', alignItems: 'center', gap: '6px' },
  checkbox: {
    width: '14px', height: '14px', flexShrink: 0,
    border: '1.5px solid #ccc', borderRadius: '3px',
    background: 'transparent', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 0,
  },
  checkboxDone: {
    background: SESSION_COLOR, borderColor: SESSION_COLOR,
  },
  checkmark: { fontSize: '9px', color: '#fff', lineHeight: 1 },
  input: {
    flex: 1, border: 'none', outline: 'none', background: 'transparent',
    fontSize: '12px', color: 'var(--text-primary)', lineHeight: '1.5', padding: '2px 0',
  },
  ownerBadges: { display: 'flex', gap: '3px', flexShrink: 0 },
  badge: {
    width: '18px', height: '18px', borderRadius: '9px',
    fontSize: '9px', fontWeight: 700, border: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', lineHeight: 1, flexShrink: 0,
  },
  badgeActive: { background: SESSION_COLOR, color: '#fff' },
  badgeInactive: { background: '#f0ecf4', color: '#9b9b9b', border: '1px solid #ddd' },
  addBtn: {
    background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px',
    color: SESSION_COLOR, padding: '2px 0', marginLeft: '20px', marginTop: '2px', textAlign: 'left',
  },
}
